import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Star, Send, Pencil, Trash2, Search, Loader2 } from 'lucide-react';

const CARD = 'bg-white border border-slate-200 shadow-sm';
const OPERATORS = [{ value: 'mtn', label: 'MTN' }, { value: 'orange', label: 'Orange' }];
const EMPTY = { name: '', phone: '', operator: 'mtn' };

const fmtPhone = (p) => {
  if (!p) return '';
  const c = p.replace(/\s/g, '');
  if (c.startsWith('+237') && c.length >= 12) return `+237 ${c.slice(4, 7)} ${c.slice(7, 10)} ${c.slice(10)}`;
  return p;
};

export default function ContactsManager({ beneficiaries, setBeneficiaries, onSend }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, phone: b.phone, operator: b.operator || 'mtn' }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Nom et numéro requis'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/mboasend/beneficiaries/${editing.id}`, form);
      } else {
        await api.post('/mboasend/beneficiaries', { ...form, country: 'CM', email: '' });
      }
      const r = await api.get('/mboasend/beneficiaries');
      setBeneficiaries(r.data.beneficiaries || []);
      setOpen(false);
      toast.success(editing ? 'Contact modifié' : 'Contact ajouté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer ${b.name} ?`)) return;
    try {
      await api.delete(`/mboasend/beneficiaries/${b.id}`);
      setBeneficiaries((prev) => prev.filter((x) => x.id !== b.id));
      toast.success('Supprimé');
    } catch { toast.error('Erreur'); }
  };

  const toggleFav = async (b) => {
    try {
      const res = await api.post(`/mboasend/beneficiaries/${b.id}/toggle-favorite`);
      setBeneficiaries((prev) => [...prev.map((x) => x.id === b.id ? { ...x, favorite: res.data.favorite } : x)]
        .sort((a, c) => (c.favorite ? 1 : 0) - (a.favorite ? 1 : 0)));
    } catch { toast.error('Erreur'); }
  };

  const filtered = beneficiaries.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.name.toLowerCase().includes(q) || (b.phone || '').includes(q);
  });

  return (
    <div data-testid="contacts-manager">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-[#0D2612] flex items-center gap-1.5" style={{ fontFamily: 'Outfit' }}>
          <Users className="w-4 h-4 text-[#006400]" /> Mes contacts
        </h2>
        <Button size="sm" onClick={openAdd} className="bg-[#006400] text-white hover:bg-[#004D00] h-8 text-xs gap-1" data-testid="add-contact-btn">
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </Button>
      </div>

      {beneficiaries.length === 0 ? (
        <div className={`${CARD} rounded-xl p-6 text-center`}>
          <Users className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Aucun contact enregistré</p>
          <button onClick={openAdd} className="text-[11px] text-[#006400] hover:underline mt-1" data-testid="add-first-contact">Ajouter un destinataire →</button>
        </div>
      ) : (
        <div className="space-y-2">
          {beneficiaries.length > 4 && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un contact..."
                className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm text-[#0D2612] placeholder:text-slate-400 outline-none focus:border-[#006400]"
                data-testid="contacts-search" />
            </div>
          )}
          {filtered.map((b) => (
            <div key={b.id} className={`${CARD} rounded-xl p-3 flex items-center gap-3`} data-testid={`contact-${b.id}`}>
              <button onClick={() => toggleFav(b)} className="flex-shrink-0 p-0.5" data-testid={`contact-fav-${b.id}`}>
                <Star className={`w-4 h-4 ${b.favorite ? 'text-[#B8860B] fill-[#B8860B]' : 'text-slate-300 hover:text-[#B8860B]'}`} />
              </button>
              <div className="w-9 h-9 rounded-full bg-[#006400]/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[#006400] font-bold text-sm">{b.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0D2612] truncate">{b.name}</p>
                <p className="text-[10px] text-slate-500">{fmtPhone(b.phone)} · {(b.operator || 'mtn').toUpperCase()}{b.send_count > 0 ? ` · ${b.send_count}x` : ''}</p>
              </div>
              <button onClick={() => onSend(b)} className="w-8 h-8 rounded-lg bg-[#006400] flex items-center justify-center flex-shrink-0 hover:bg-[#004D00]" data-testid={`contact-send-${b.id}`}>
                <Send className="w-3.5 h-3.5 text-white" />
              </button>
              <button onClick={() => openEdit(b)} className="text-slate-400 hover:text-[#006400] flex-shrink-0 p-1" data-testid={`contact-edit-${b.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => remove(b)} className="text-slate-400 hover:text-red-600 flex-shrink-0 p-1" data-testid={`contact-delete-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D2612]" style={{ fontFamily: 'Outfit' }}>{editing ? 'Modifier le contact' : 'Nouveau contact'}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Enregistrez un destinataire pour envoyer plus vite.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Nom complet</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jean Dupont"
                className="bg-white border-slate-300 focus:border-[#006400] h-11 text-[#0D2612]" data-testid="contact-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Numéro Mobile Money</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+237 6XX XXX XXX"
                className="bg-white border-slate-300 focus:border-[#006400] h-11 text-[#0D2612]" data-testid="contact-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Opérateur</Label>
              <div className="grid grid-cols-2 gap-2">
                {OPERATORS.map((op) => (
                  <button key={op.value} onClick={() => setForm({ ...form, operator: op.value })}
                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${form.operator === op.value ? 'border-[#006400] bg-[#006400]/10 text-[#006400]' : 'border-slate-200 bg-[#F5F4EF] text-slate-600 hover:border-[#006400]/50'}`}
                    data-testid={`contact-operator-${op.value}`}>
                    {op.label} Money
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="w-full bg-[#006400] text-white hover:bg-[#004D00] h-11" data-testid="contact-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Enregistrer' : 'Ajouter le contact')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
